import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CreditCard, LogOut, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { createClient } from '@supabase/supabase-js';

export const Header = () => {
  const { user, credits, signOut, isAdmin, subscriptionActive } = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user?.id) return;
      const supabase = createClient(
        import.meta.env.VITE_SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_ANON_KEY
      );
      const { data, error } = await supabase
        .from('user_credits')
        .select('stripe_customer_id')
        .eq('user_id', user.id)
        .single();
      setHasSubscription(!!data?.stripe_customer_id);
    };
    checkSubscription();
  }, [user]);

  const handleSignOut = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isSigningOut) return;

    try {
      setIsSigningOut(true);
      await signOut();
      // Po úspěšném odhlášení přesměrujeme na hlavní stránku
      window.location.href = '/';
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Nepodařilo se odhlásit. Prosím zkuste to znovu.");
    } finally {
      // Small delay before resetting the loading state
      setTimeout(() => {
        setIsSigningOut(false);
      }, 200);
    }
  };

  return (
    <header className="border-b">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <img src="/images/open_logo.png" alt="Openstudio logo" className="h-16 w-auto" />
        </Link>
        
        <nav className="hidden md:flex items-center space-x-6 mr-6">
          <Link to="/" className="text-gray-700 hover:text-[#14A48D] transition-colors">
            Domů
          </Link>
          <Link to="/cenik" className="text-gray-700 hover:text-[#14A48D] transition-colors">
            Ceník
          </Link>
          <Link to="/examples" className="text-gray-700 hover:text-[#14A48D] transition-colors">
            Ukázky
          </Link>
        </nav>
        
        <div className="flex items-center space-x-4">
          {user ? (
            <>
              <div className="flex items-center bg-gray-100 rounded-full px-3 py-1">
                <CreditCard className="h-4 w-4 text-[#14A48D] mr-1" />
                <span className="text-sm font-medium">{credits} Kreditů</span>
              </div>
              
              {isAdmin && (
                <Link to="/admin">
                  <Button variant="outline" size="sm" className="hover:bg-[#14A48D] hover:text-white transition-colors">Admin</Button>
                </Link>
              )}
              
              <Button variant="outline" size="sm" onClick={() => { window.location.href = "/projects"; }} className="hover:bg-[#14A48D] hover:text-white transition-colors">Moje projekty</Button>
              
              {subscriptionActive === 0 && (
                <Link to="/buy-credits">
                  <Button variant="outline" size="sm" className="hover:bg-[#14A48D] hover:text-white transition-colors">Koupit kredity</Button>
                </Link>
              )}
              {subscriptionActive === 1 && (
                <Link to="/account">
                  <Button variant="outline" size="sm" className="hover:bg-[#14A48D] hover:text-white transition-colors">Můj účet</Button>
                </Link>
              )}
              
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleSignOut}
                disabled={isSigningOut}
                className="flex items-center gap-2 hover:bg-[#14A48D] hover:text-white transition-colors"
              >
                {isSigningOut ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <LogOut className="h-4 w-4" />
                )}
                {isSigningOut ? "Odhlášení..." : "Odhlásit se"}
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="outline" size="sm" className="hover:bg-[#14A48D] hover:text-white transition-colors">Přihlásit se</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-[#14A48D] hover:bg-[#11997C] text-white font-bold transition-colors">Registrace</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
